import { useCallback, useEffect, useRef, useState } from 'react';
import { getAccessToken, isLoggedIn } from '../lib/auth';
import { ORG_DOMAINS } from '../lib/config';
import { domainOf, readCurrentEmail } from '../lib/office';
import {
  getCrmMatch,
  logEmail,
  logThread,
  restoreCompany,
  restorePerson,
  updateCompany,
  updatePerson,
  type LogInput,
  type NewCompanyFields,
  type NewPersonFields,
} from '../lib/twentyClient';
import { fetchConversation, isThreadLoggingSupported } from '../lib/graph';
import type { CrmMatch, CurrentEmail } from '../lib/types';
import { DEFAULT_STAGE } from '../lib/categories';
import {
  loadFieldOptions,
  FALLBACK_FIELD_OPTIONS,
  type FieldOptions,
} from '../lib/fieldOptions';
import { RecordSection, type RecordFieldDef } from './RecordSection';
import { CategoryPicker } from './CategoryPicker';
import './styles.css';

type Status =
  | 'loading'
  | 'needsAuth'
  | 'ready'
  | 'logging'
  | 'logged'
  | 'blocked' // add-in is running in a non-CLS mailbox
  | 'error';

type Fields = Record<string, string>;

const capitalize = (value: string): string =>
  value ? value[0].toUpperCase() + value.slice(1) : '';

// Best-effort first/last name from an email local part (new-record prefill).
const nameFromEmail = (address: string): { firstName: string; lastName: string } => {
  const local = address.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  return {
    firstName: capitalize(parts[0] ?? ''),
    lastName: capitalize(parts.slice(1).join(' ')),
  };
};

const companyNameFromDomain = (domain: string): string =>
  capitalize(domain.split('.')[0] ?? '');

export const App = () => {
  const [status, setStatus] = useState<Status>('loading');
  const [email, setEmail] = useState<CurrentEmail | null>(null);
  const [match, setMatch] = useState<CrmMatch | null>(null);
  const [personFields, setPersonFields] = useState<Fields>({});
  const [companyFields, setCompanyFields] = useState<Fields>({});
  const [companyCategory, setCompanyCategory] = useState<string[]>([]);
  const [companyStage, setCompanyStage] = useState<string>(DEFAULT_STAGE);
  const [personCategory, setPersonCategory] = useState<string[]>([]);
  // Category/stage options are loaded live from the platform field definitions
  // (so platform CRUD flows through). Starts as the built-in fallback.
  const [fieldOptions, setFieldOptions] = useState<FieldOptions>(
    FALLBACK_FIELD_OPTIONS,
  );
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  // Custom success detail (e.g. how many thread messages were logged).
  const [result, setResult] = useState('');
  // The email we've already loaded the form for. Guards against the pinned
  // pane re-firing ItemChanged for the SAME email and wiping in-progress edits.
  const loadedIdRef = useRef('');

  const loadMatch = useCallback(async (current: CurrentEmail) => {
    const token = await getAccessToken();
    const [result, options] = await Promise.all([
      getCrmMatch(token, current),
      loadFieldOptions(token),
    ]);
    setMatch(result);
    setFieldOptions(options);

    const person = result.person;
    const fallbackName = person
      ? { firstName: '', lastName: '' }
      : nameFromEmail(result.contactEmail);
    setPersonFields({
      firstName: person?.firstName ?? fallbackName.firstName,
      lastName: person?.lastName ?? fallbackName.lastName,
      email: person?.email ?? result.contactEmail ?? '',
      jobTitle: person?.jobTitle ?? '',
      phone: person?.phone ?? result.guessPhone ?? '',
      company: person?.companyName ?? result.company?.name ?? '',
    });

    const company = result.company;
    setCompanyFields({
      name:
        company?.name ??
        (result.canCreateCompany ? companyNameFromDomain(result.domain) : ''),
      domain: company?.domain ?? result.domain ?? '',
      city: company?.city ?? '',
    });
    // Category/stage prefill: from the matched record, or empty/first-stage for
    // a new one (≥1 company category is required, so a new company starts empty
    // on purpose). Category is MULTI_SELECT, so these are arrays. New-company
    // stage defaults to the platform's first lifecycle option (PROSPECT today).
    setCompanyCategory(company?.category ?? []);
    setCompanyStage(
      company?.lifecycleStage ?? options.companyStage[0]?.value ?? DEFAULT_STAGE,
    );
    setPersonCategory(person?.category ?? []);

    loadedIdRef.current = current.internetMessageId;
    setStatus(result.alreadyLogged ? 'logged' : 'ready');
  }, []);

  const load = useCallback(async () => {
    try {
      const current = await readCurrentEmail();
      // Pinned pane: Outlook can re-fire ItemChanged for the SAME email. Skip
      // reloading in that case so we don't reset the form and wipe edits.
      if (
        current.internetMessageId &&
        current.internetMessageId === loadedIdRef.current
      ) {
        return;
      }
      setStatus('loading');
      setNote('');
      setResult('');
      setEmail(current);
      // Guard: only log from a CLS Wind mailbox (multi-account safety).
      if (!ORG_DOMAINS.includes(domainOf(current.mailboxOwner) ?? '')) {
        setStatus('blocked');
        return;
      }
      if (isLoggedIn()) await loadMatch(current);
      else setStatus('needsAuth');
    } catch (caught) {
      setError((caught as Error).message);
      setStatus('error');
    }
  }, [loadMatch]);

  useEffect(() => {
    load();
    // Guard the Office API: in a plain browser, and in some hosts (notably new
    // Outlook for Windows) before Office finishes initialising, mailbox is
    // undefined - an unguarded call here crashes the whole pane to a blank
    // screen. When pinned, ItemChanged re-reads on message switch.
    const mailbox = Office.context?.mailbox;
    if (!mailbox) return;
    const handler = () => load();
    mailbox.addHandlerAsync(Office.EventType.ItemChanged, handler);
    return () => mailbox.removeHandlerAsync(Office.EventType.ItemChanged);
  }, [load]);

  const handleConnect = async () => {
    if (!email) return;
    try {
      setStatus('loading');
      await getAccessToken(true);
      await loadMatch(email);
    } catch (caught) {
      setError((caught as Error).message);
      setStatus('error');
    }
  };

  const savePerson = async () => {
    if (!email || !match?.person) return;
    const token = await getAccessToken();
    // A trashed match must be restored before it can be edited.
    if (match.person.deleted) await restorePerson(token, match.person.id);
    await updatePerson(token, match.person.id, {
      firstName: personFields.firstName ?? '',
      lastName: personFields.lastName ?? '',
      jobTitle: personFields.jobTitle ?? '',
      email: personFields.email ?? match.person.email,
      phoneNumber: personFields.phone ?? '',
      phoneCallingCode: match.person.phoneCallingCode,
      phoneCountryCode: match.person.phoneCountryCode,
      category: personCategory,
    });
    await loadMatch(email);
  };

  const saveCompany = async () => {
    if (!email || !match?.company) return;
    const token = await getAccessToken();
    if (match.company.deleted) await restoreCompany(token, match.company.id);
    await updateCompany(token, match.company.id, {
      name: companyFields.name ?? match.company.name,
      city: companyFields.city ?? '',
      category: companyCategory,
      lifecycleStage: companyStage,
    });
    await loadMatch(email);
  };

  // Save the user's edits to the matched records and assemble the LogInput.
  // Shared by single-email and whole-thread logging.
  const prepareLogInput = useCallback(
    async (token: string): Promise<LogInput | null> => {
      if (!match) return null;

      // Existing record: save the user's edits too (so what they see is what
      // gets saved). New record: prepare it for creation.
      let newPerson: NewPersonFields | null = null;
      if (match.person) {
        // Restore a trashed match before linking — re-creating would collide
        // with the unique index the trashed row still holds.
        if (match.person.deleted) await restorePerson(token, match.person.id);
        await updatePerson(token, match.person.id, {
          firstName: personFields.firstName ?? '',
          lastName: personFields.lastName ?? '',
          jobTitle: personFields.jobTitle ?? '',
          email: personFields.email ?? match.person.email,
          phoneNumber: personFields.phone ?? '',
          phoneCallingCode: match.person.phoneCallingCode,
          phoneCountryCode: match.person.phoneCountryCode,
          category: personCategory,
        });
      } else if (!match.internal) {
        const contactEmail = personFields.email || match.contactEmail;
        if (contactEmail) {
          newPerson = {
            firstName: personFields.firstName ?? '',
            lastName: personFields.lastName ?? '',
            email: contactEmail,
            jobTitle: personFields.jobTitle ?? '',
            phoneNumber: personFields.phone ?? '',
            category: personCategory,
          };
        }
      }

      let newCompany: NewCompanyFields | null = null;
      if (match.company) {
        if (match.company.deleted) await restoreCompany(token, match.company.id);
        await updateCompany(token, match.company.id, {
          name: companyFields.name ?? match.company.name,
          city: companyFields.city ?? '',
          category: companyCategory,
          lifecycleStage: companyStage,
        });
      } else if (match.canCreateCompany) {
        newCompany = {
          name: companyFields.name || match.domain,
          domain: companyFields.domain || match.domain,
          city: companyFields.city ?? '',
          category: companyCategory,
          lifecycleStage: companyStage,
        };
      }

      return {
        loggerNote: note.trim() || undefined,
        personId: match.person?.id ?? null,
        newPerson,
        companyId: match.company?.id ?? null,
        newCompany,
        personHasCompany: !!match.person?.companyName,
      };
    },
    [match, personFields, companyFields, personCategory, companyCategory, companyStage, note],
  );

  const handleLog = async () => {
    if (!email || !match) return;
    try {
      setStatus('logging');
      const token = await getAccessToken();
      const input = await prepareLogInput(token);
      if (!input) return;
      await logEmail(token, email, input);
      // Refresh so the cards reflect saved/created records in the logged state.
      await loadMatch(email);
    } catch (caught) {
      setError((caught as Error).message);
      setStatus('error');
    }
  };

  // Log the whole Outlook conversation in one action (reads sibling messages via
  // Microsoft Graph). Falls back to logging just the open email if Graph returns
  // nothing (single-message conversation, or an unsupported host).
  const handleLogThread = async () => {
    if (!email || !match) return;
    try {
      setStatus('logging');
      const token = await getAccessToken();
      const input = await prepareLogInput(token);
      if (!input) return;
      const messages = await fetchConversation(
        email.conversationId,
        email.mailboxOwner,
      );
      if (messages.length === 0) {
        await logEmail(token, email, input);
        setResult('Logged this email.');
      } else {
        const res = await logThread(token, email, messages, input);
        setResult(
          `Logged ${res.logged} new of ${res.total} message${res.total === 1 ? '' : 's'} in this thread.`,
        );
      }
      await loadMatch(email);
    } catch (caught) {
      setError((caught as Error).message);
      setStatus('error');
    }
  };

  if (status === 'loading') {
    return <div className="pane center">Loading…</div>;
  }
  if (status === 'error') {
    return (
      <div className="pane">
        <div className="banner error">Something went wrong</div>
        <p className="muted">{error}</p>
        <button onClick={() => window.location.reload()}>Try again</button>
      </div>
    );
  }

  const personDefs: RecordFieldDef[] = [
    { key: 'firstName', label: 'First name', editable: true },
    { key: 'lastName', label: 'Last name', editable: true },
    { key: 'email', label: 'Email', editable: true },
    { key: 'jobTitle', label: 'Job title', editable: true },
    { key: 'phone', label: 'Phone', editable: true },
    { key: 'company', label: 'Company', editable: false },
  ];
  // Domain is editable only when creating (a partial update would wipe a
  // company's secondary links).
  const companyDefs: RecordFieldDef[] = [
    { key: 'name', label: 'Name', editable: true },
    { key: 'domain', label: 'Domain', editable: !match?.company },
    { key: 'city', label: 'City', editable: true },
  ];

  const personSummary = match?.person
    ? `${match.person.firstName} ${match.person.lastName}`.trim() +
      (match.person.companyName ? ` · ${match.person.companyName}` : '')
    : '';
  const companySummary = match?.company?.name ?? '';

  // A company is involved when one is matched or will be created. Category is
  // required in that case - the log button stays disabled until it's set.
  const companyInvolved = !!(match?.company || match?.canCreateCompany);
  const categoryMissing = companyInvolved && companyCategory.length === 0;
  // "Log thread" needs Microsoft Graph via NAA, unsupported on some older hosts.
  const threadSupported = isThreadLoggingSupported();

  return (
    <div className="pane">
      <h1>Log to Breeze</h1>

      {email && (
        <div className="card">
          <div className="subject">{email.subject}</div>
          <div className="muted">
            <span className={`chip ${email.direction.toLowerCase()}`}>
              {email.direction === 'INBOUND' ? 'Received' : 'Sent'}
            </span>
            {match?.contactEmail ||
              (email.direction === 'INBOUND'
                ? email.fromEmail
                : email.toEmails[0] ?? '')}
          </div>
        </div>
      )}

      {status === 'blocked' && (
        <div className="banner error">
          Logging is only available from your CLS Wind mailbox. This add-in is
          running in <strong>{email?.mailboxOwner}</strong>, so saving to Breeze is disabled here.
        </div>
      )}

      {status === 'needsAuth' && (
        <>
          <p className="muted">
            Connect your Breeze account to log this email.
          </p>
          <button className="primary" onClick={handleConnect}>
            Connect to Breeze
          </button>
        </>
      )}

      {match &&
        (status === 'ready' || status === 'logging' || status === 'logged') && (
        <>
          {match.automatedSender && (
            <div className="banner hint">
              {match.fromSignature
                ? `Automated sender — using ${match.contactEmail} from the signature.`
                : 'Automated sender — no contact found in the signature. Check the details below.'}
            </div>
          )}

          {match.internal ? (
            <div className="banner">
              Internal email — no contact or company will be created.
            </div>
          ) : (
            <>
              <RecordSection
                title="Contact"
                status={match.person ? 'found' : 'new'}
                summary={personSummary}
                fields={personDefs}
                values={personFields}
                onChange={(key, value) =>
                  setPersonFields((prev) => ({ ...prev, [key]: value }))
                }
                onSave={match.person ? savePerson : undefined}
                note={
                  match.person?.deleted
                    ? 'In your CRM trash — logging or saving will restore this contact.'
                    : undefined
                }
              />
              <CategoryPicker
                label="Contact category"
                options={fieldOptions.personCategory}
                value={personCategory}
                onChange={setPersonCategory}
              />
              <RecordSection
                title="Company"
                status={match.company ? 'found' : 'new'}
                summary={companySummary}
                fields={companyDefs}
                values={companyFields}
                onChange={(key, value) =>
                  setCompanyFields((prev) => ({ ...prev, [key]: value }))
                }
                onSave={match.company ? saveCompany : undefined}
                note={
                  match.company?.deleted
                    ? 'In your CRM trash — logging or saving will restore this company.'
                    : undefined
                }
              />
              {companyInvolved && (
                <>
                  <CategoryPicker
                    label="Company category"
                    required
                    options={fieldOptions.companyCategory}
                    value={companyCategory}
                    onChange={setCompanyCategory}
                  />
                  <label className="picker">
                    <span className="picker-label">Lifecycle stage</span>
                    <select
                      className="stage-select"
                      value={companyStage}
                      onChange={(event) => setCompanyStage(event.target.value)}
                    >
                      {fieldOptions.companyStage.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
            </>
          )}

          {status === 'logged' ? (
            <div className="banner success">
              {result ||
                (match.alreadyLogged
                  ? 'This email is logged in Breeze.'
                  : 'Logged to Breeze.')}
            </div>
          ) : (
            <>
              <label className="field">
                <span>Note (optional)</span>
                <textarea
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Add context for your team…"
                  rows={3}
                />
              </label>
              <button
                className="primary"
                disabled={status === 'logging' || categoryMissing}
                onClick={handleLog}
              >
                {status === 'logging' ? 'Logging…' : 'Log this email'}
              </button>
              {threadSupported && (
                <button
                  className="secondary"
                  disabled={status === 'logging' || categoryMissing}
                  onClick={handleLogThread}
                  title="Log every message in this Outlook conversation"
                >
                  Log entire thread
                </button>
              )}
              {categoryMissing && (
                <p className="muted center">
                  Pick a company category to log.
                </p>
              )}
            </>
          )}
        </>
      )}
      <div className="version">Breeze add-in v{__APP_VERSION__}</div>
    </div>
  );
};
